import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  LoadingState,
  PORTAL_PAGE_TITLE_ID,
} from '@beautyburst/ui';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AdminApiClient, AdminRecord, AdminStoreLocation } from '../api-client.ts';
import { AdminApiError } from '../http-client.ts';

const PAGE_SIZE = 100;

const ROLE_GROUPS = [
  {
    label: 'Beauty Burst staff & admin',
    options: [
      { value: 'admin.operations', label: 'Operations' },
      { value: 'admin.finance', label: 'Finance' },
      { value: 'admin.support', label: 'Support' },
      { value: 'admin.readonly', label: 'Read-only' },
    ],
  },
  {
    label: 'Retailer / store owner',
    options: [
      { value: 'retailer.owner', label: 'Store owner' },
      { value: 'retailer.manager', label: 'Store manager' },
      { value: 'retailer.staff', label: 'Store staff' },
      { value: 'retailer.finance', label: 'Store finance' },
    ],
  },
  {
    label: 'Courier',
    options: [
      { value: 'courier.integration', label: 'Delivery integration (requires enabled courier)' },
    ],
  },
] as const;

function text(row: AdminRecord, key: string): string {
  const value = row[key];
  return value === null || value === undefined ? '—' : String(value);
}

function errorText(error: unknown): string {
  return error instanceof AdminApiError
    ? error.message
    : 'The administrator service did not return data. Try again.';
}

type RoleValue = (typeof ROLE_GROUPS)[number]['options'][number]['value'];

function roleLabel(roleKey: string): string {
  for (const group of ROLE_GROUPS) {
    const match = group.options.find((option) => option.value === roleKey);
    if (match !== undefined) return match.label;
  }
  return roleKey;
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function retailerOptions(stores: readonly AdminStoreLocation[]) {
  const byId = new Map<string, string>();
  for (const store of stores) byId.set(store.retailer.id, store.retailer.name);
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

function formatExpiry(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function InvitationsScreen({ client }: { readonly client: AdminApiClient }) {
  const [invitations, setInvitations] = useState<readonly AdminRecord[] | null>(null);
  const [stores, setStores] = useState<readonly AdminStoreLocation[] | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleValue>('admin.operations');
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setFailure(null);
    try {
      const result = await client.invitations.list({ limit: PAGE_SIZE, offset: 0 });
      setInvitations(result.invitations);
      try {
        setStores(await client.stores.list());
      } catch {
        setStores([]);
      }
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const retailers = useMemo(() => retailerOptions(stores ?? []), [stores]);
  const retailerRole = selectedRole.startsWith('retailer.');

  const submitInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    const reason = String(form.get('reason') ?? '').trim();
    const expiresInDaysRaw = String(form.get('expiresInDays') ?? '').trim();
    if (email === '' || reason === '' || (retailerRole && selectedRetailerId === '')) return;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      await client.invitations.create({
        email,
        roleKey: selectedRole,
        reason,
        ...(retailerRole ? { retailerId: selectedRetailerId } : {}),
        ...(expiresInDaysRaw === '' ? {} : { expiresInDays: Number(expiresInDaysRaw) }),
      });
      setNotice(
        `Invitation sent to ${email}. The invitee will receive a link to set up their account.`,
      );
      await load();
      event.currentTarget.reset();
      setSelectedRole('admin.operations');
      setSelectedRetailerId('');
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invitationId: string, email: string) => {
    const reason = window.prompt(`Reason for revoking the invitation to ${email}?`)?.trim() ?? '';
    if (reason === '') return;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      await client.invitations.revoke(invitationId, reason);
      setNotice(`Invitation to ${email} revoked.`);
      await load();
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  if (loading && invitations === null) return <LoadingState label="Loading invitations" />;
  if (failure !== null && invitations === null)
    return (
      <ErrorState
        title="Invitations unavailable"
        description={failure}
        retry={<Button onClick={() => void load()}>Try again</Button>}
      />
    );

  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <p>
        Invite a teammate or retailer partner by email. They receive a single-use link to set a
        password and join with the role you choose. Existing accounts should be granted a role from
        the Users and roles screen instead.
      </p>

      {notice === null ? null : (
        <Card>
          <p role="status">{notice}</p>
        </Card>
      )}
      {failure === null ? null : (
        <Card>
          <p role="alert">{failure}</p>
        </Card>
      )}

      <Card>
        <h2>Send an invitation</h2>
        <form className="admin-inline-form" onSubmit={(event) => void submitInvite(event)}>
          <Field
            id="invitation-email"
            name="email"
            label="Email"
            type="email"
            required
            autoComplete="email"
            disabled={busy}
          />
          <label>
            Role
            <select
              name="roleKey"
              value={selectedRole}
              onChange={(event) => {
                const value = event.currentTarget.value as RoleValue;
                setSelectedRole(value);
                if (!value.startsWith('retailer.')) setSelectedRetailerId('');
              }}
              required
              disabled={busy}
            >
              {ROLE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          {retailerRole ? (
            <label>
              Retailer
              <select
                name="retailerId"
                value={selectedRetailerId}
                onChange={(event) => setSelectedRetailerId(event.currentTarget.value)}
                required
                disabled={busy}
              >
                <option value="">Select a retailer</option>
                {retailers.map((retailer) => (
                  <option key={retailer.id} value={retailer.id}>
                    {retailer.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <Field
            id="invitation-expiry"
            name="expiresInDays"
            label="Expires in (days, optional)"
            type="number"
            min={1}
            max={30}
            placeholder="7"
            disabled={busy}
          />
          <Field id="invitation-reason" name="reason" label="Reason" required disabled={busy} />
          <Button type="submit" disabled={busy} style={{ minWidth: '44px', minHeight: '44px' }}>
            {busy ? 'Sending…' : 'Send invitation'}
          </Button>
        </form>
      </Card>

      <Card>
        <h2>Invitations</h2>
        <DataTable
          caption="All account invitations"
          rows={invitations ?? []}
          rowKey={(invitation) => text(invitation, 'id')}
          emptyMessage="No invitations have been sent yet."
          columns={[
            {
              key: 'email',
              header: 'Email',
              render: (invitation) => text(invitation, 'email'),
            },
            {
              key: 'role',
              header: 'Role',
              render: (invitation) => roleLabel(text(invitation, 'role_key')),
            },
            {
              key: 'status',
              header: 'Status',
              render: (invitation) => statusLabel(text(invitation, 'status')),
            },
            {
              key: 'expires',
              header: 'Expires',
              render: (invitation) => {
                const value = text(invitation, 'expires_at');
                return value === '—' ? '—' : formatExpiry(value);
              },
            },
            {
              key: 'action',
              header: 'Action',
              render: (invitation) => {
                const status = text(invitation, 'status');
                if (status !== 'PENDING') return statusLabel(status);
                return (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={busy}
                    onClick={() => void revoke(text(invitation, 'id'), text(invitation, 'email'))}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    Revoke
                  </Button>
                );
              },
            },
          ]}
        />
      </Card>
    </section>
  );
}
